/// <amd-module name="VSSUI/Components/IdentityCard/IdentityCard" />
/**
 * The IdentityCard is intended to show contact and organization information for an identity.
 * You may pass the entity directly or you may pass a unique attribute (e.g. uniqueName, entityID, signInAddress) as a prop.
 *
 */

import * as React from "react";

import { Callout } from "VSSUI/Callout";
import { IIdentity, IIdentityConnections } from "../Persona/Persona.Props";
import { CardType, IDataState, IdentityCardProps } from "./IdentityCard.Props";
import { IdentityCardContent } from "./IdentityCardContent";
import * as Utils from "./IdentityCardIdentityUtils";

export interface IdentityCardState {
    /** Mimics stack to allow for breadcrumb */
    dataState: IDataState;

    /** Prevent requests if working */
    working: boolean;

    /** Set to true when complete identity information could not be loaded */
    showUnknownUser: boolean;
}

export class IdentityCard extends React.Component<IdentityCardProps, IdentityCardState> {
    private calloutRef = React.createRef<Callout>();
    // used to stop processing callbacks if the card is dismissed before data loads
    private dismissed: boolean;

    constructor(props: IdentityCardProps) {
        super(props);

        // Setup state data and history
        const initialDataState: IDataState = {
            identity: props.identity,
            managerList: undefined,
            directReportList: undefined,
            previousDataState: undefined,
            cardType: CardType.Default,
            header: props.initialHeader ? props.initialHeader.identity : undefined,
            displayName: props.displayName,
            imageUrl: props.imageUrl,
            email: this.getEmail(),
            isGroup: Utils.isGroup(props.identity)
        };
        let moreWorkNeeded = true;

        if (!props.identity && !props.uniqueAttribute && props.displayName) {
            // All we have is displayName and may be imageUrl. Simply go ahead and setup the state for displaying the card without any identity call.
            initialDataState.identity = {
                entityId: "",
                entityType: "user",
                originDirectory: "vsd",
                originId: "",
                displayName: props.displayName,
                image: props.imageUrl,
                signInAddress: initialDataState.email
            };
            moreWorkNeeded = false;
        }

        this.state = {
            dataState: initialDataState,
            showUnknownUser: false,
            working: moreWorkNeeded
        };
    }

    public componentDidMount(): void {
        if (this.state.working) {
            this.setupInitialData(this.props.uniqueAttribute);
        }
    }

    public componentDidUpdate(): void {
        // If the content was updated, we may need to update the callout so it is positioned correctly.
        this.calloutRef.current && !this.state.working && this.calloutRef.current.updateLayout();
    }

    public componentWillUnmount(): void {
        this.dismissed = true;
    }

    // Render
    public render(): JSX.Element | null {
        return this.props.uniqueAttribute || this.props.identity || this.props.displayName ? (
            <IdentityCardContent
                {...this.props}
                dataProps={this.state.dataState}
                onClickEntity={this.onClickEntity}
                onDismissCallback={this.onDismissCallback}
                onShowContactCard={this.onShowContactCard}
                onShowOrganizationCard={this.onShowOrganizationCard}
                working={this.state.working}
                onHeaderClick={this.headerOnClickHandler}
                calloutRef={this.calloutRef}
                showUnknownUser={this.state.showUnknownUser}
            />
        ) : null;
    }

    private onDismissCallback = (): void => {
        this.props.onDismissCallback && this.props.onDismissCallback();
    };

    // Handle going back
    private headerOnClickHandler = (): void => {
        if (this.state.dataState.previousDataState) {
            this.setState({
                dataState: this.state.dataState.previousDataState,
                working: false
            });
        } else if (this.props.initialHeader && this.props.initialHeader.onClickFunction) {
            this.props.initialHeader.onClickFunction();
        }
    };

    private onShowContactCard = (): void => {
        const newDataState: IDataState = {
            ...this.state.dataState,
            previousDataState: this.state.dataState,
            cardType: CardType.Contact,
            header: this.state.dataState.identity
        };
        this.setState({
            dataState: newDataState
        });
    };

    private onShowOrganizationCard = (): void => {
        // Do not handle click event if working
        if (this.state.working || this.dismissed) {
            return;
        }
        const currentDataState = this.state.dataState;

        if (currentDataState.isGroup) {
            // Only make the call if we don't have the data already
            const currentDataState = this.state.dataState;
            const newDataState: IDataState = {
                ...currentDataState,
                cardType: CardType.Organization,
                header: currentDataState.identity,
                previousDataState: currentDataState
            };
            this.setState({
                dataState: newDataState
            });

            if (!currentDataState.successors || currentDataState.successors.length <= 1) {
                this.setState({
                    working: true
                });
                this.resolveIdentity(newDataState, this.props.onRequestConnectionInformation(currentDataState.identity));
            }
        } else if (currentDataState.managerList && currentDataState.managerList.length <= 1) {
            // Only make the call if we don't have the data already
            const currentDataState = this.state.dataState;
            const newDataState: IDataState = {
                ...currentDataState,
                cardType: CardType.Organization,
                header: currentDataState.identity,
                previousDataState: currentDataState
            };
            this.setState({
                dataState: newDataState,
                working: true
            });

            this.resolveIdentity(newDataState, this.props.onRequestConnectionInformation(currentDataState.identity, true));
        } else {
            // Already have the data so we can present
            const currentDataState = this.state.dataState;
            const newDataState: IDataState = {
                ...currentDataState,
                cardType: CardType.Organization,
                header: currentDataState.identity,
                previousDataState: currentDataState
            };
            this.setState({
                dataState: newDataState,
                working: false
            });
        }
    };

    // Handle entity click
    private onClickEntity = (identity: IIdentity): void => {
        // Do not handle click event if working
        if (this.state.working) {
            return;
        }

        const currentDataState = this.state.dataState;
        const newDataState: IDataState = {
            identity: identity,
            cardType: CardType.Default,
            header: currentDataState.identity,
            isGroup: Utils.isGroup(currentDataState.identity),
            directReportList: [],
            managerList: [],
            previousDataState: currentDataState,
            displayName: identity.displayName,
            imageUrl: identity.image,
            email: identity.mail
        };
        this.setState({
            dataState: newDataState,
            working: true
        });

        // API call to get identity
        this.getIdentityByUniqueAttribute(identity);
    };

    // Helper method to get initial data (which includes only direct manager)
    private setupInitialData(uniqueAttribute?: string): void {
        const dataState = this.state.dataState;
        if (!dataState.identity && uniqueAttribute) {
            // Get identity first, then get connections in callback
            this.getIdentityByUniqueAttribute(uniqueAttribute);
        } else if (dataState.identity && !Utils.isCompleteIdentity(dataState.identity)) {
            // Seems to be cached, refetch identity by unique attribute (entityId)
            this.getIdentityByUniqueAttribute(dataState.identity);
        } else {
            if (Utils.isGroup(dataState.identity) && dataState.successors && dataState.successors.length > 0) {
                this.resolveIdentity(dataState, { successors: dataState.successors });
            } else {
                this.resolveIdentity(dataState, this.props.onRequestConnectionInformation(dataState.identity));
            }
        }
    }

    // Helper method to get identity information given an entity ID
    private resolveIdentity(dataState: IDataState, identity: IIdentityConnections | PromiseLike<IIdentityConnections>): void {
        const identityAsEntity: IIdentityConnections = identity as IIdentityConnections;
        const identityAsPromiseLike: PromiseLike<IIdentityConnections> = identity as PromiseLike<IIdentityConnections>;
        if (identityAsPromiseLike && identityAsPromiseLike.then) {
            identityAsPromiseLike.then((connections: IIdentityConnections) => {
                this.updateConnections(dataState, connections);
            });
        } else if (identityAsEntity) {
            this.updateConnections(dataState, identityAsEntity);
        }
    }

    // Helper method to get identity information given an entity ID
    private resolveIEntity(identity: IIdentity | PromiseLike<IIdentity>): void {
        const identityAsEntity: IIdentity = identity as IIdentity;
        const identityAsPromiseLike: PromiseLike<IIdentity> = identity as PromiseLike<IIdentity>;
        if (identityAsPromiseLike && identityAsPromiseLike.then) {
            identityAsPromiseLike.then((connections: IIdentity) => {
                this.updateEntity(connections);
            });
        } else {
            this.updateEntity(identityAsEntity);
        }
    }

    private updateConnections = (dataState: IDataState, connections: IIdentityConnections): void => {
        // Don't call if the component is unmounted.
        if (this.dismissed) {
            return;
        }
        // ensure we haven't attempted to update which card we were in before the callback happens.
        if (this.state.dataState && dataState.identity === this.state.dataState.identity) {
            this.setState({
                dataState: {
                    ...this.state.dataState,
                    managerList: connections.managers && connections.managers.reverse(),
                    directReportList: connections.directReports,
                    successors: connections.successors
                },
                working: false
            });
        }
    };

    private updateEntity = (identity: IIdentity): void => {
        if (!identity) {
            // The identity could not be found
            this.setState({
                working: false
            });
            return;
        }

        if (!Utils.isCompleteIdentity(identity, true)) {
            // The identity was found but doesn't have enough info to display
            this.setState({
                working: false,
                showUnknownUser: true
            });
            return;
        }

        const requireConnections =
            (Utils.isGroup(identity) && (!this.state.dataState.successors || this.state.dataState.successors.length <= 0)) ||
            Utils.isAadUser(identity) ||
            Utils.isAdUser(identity);
        const imageUrl: string | undefined = this.props.imageUrl ? this.props.imageUrl : identity.image;

        // Update data state and working state (visible state updated later)
        const newDataState: IDataState = {
            ...this.state.dataState,
            identity: identity,
            displayName: identity.displayName,
            imageUrl: imageUrl,
            isGroup: Utils.isGroup(identity)
        };
        // Check for authenticated users. For authenticated users, go on to make the connections call. For non-authenticated users, stop the calls and load the card with images.
        if (requireConnections) {
            this.setState({ dataState: newDataState, working: false });
            this.resolveIdentity(newDataState, this.props.onRequestConnectionInformation(newDataState.identity));
        } else {
            this.setState({ dataState: newDataState, working: false });
        }
    };

    // Helper method to get identity information given an entity ID
    private getIdentityByUniqueAttribute(identifier: string | IIdentity): void {
        let uniqueAttribute: string;
        if (typeof identifier == "string") {
            uniqueAttribute = identifier;
        } else if (Utils.isGroup(identifier) || Utils.isAadUser(identifier) || Utils.isAdUser(identifier)) {
            uniqueAttribute = identifier.entityId;
        } else {
            uniqueAttribute = identifier.signInAddress ? identifier.signInAddress : ""; // VSD users (MSA accounts)
        }

        this.resolveIEntity(this.props.getEntityFromUniqueAttribute(uniqueAttribute));
    }

    private getEmail(): string {
        if (this.props.identity) {
            // This is for displaying a fall back card when identities search doesn't fetch any identity
            if ((Utils.isAadUser(this.props.identity) || Utils.isAdUser(this.props.identity)) && this.props.identity.mail) {
                return this.props.identity.mail;
            } else if (this.props.identity && this.props.identity.signInAddress) {
                return this.props.identity.signInAddress;
            } else if (this.props.identity && this.props.identity.mail) {
                return this.props.identity.mail;
            } else if (this.props.uniqueAttribute) {
                // Check if uniqueAttribute is an email
                const parts = this.props.uniqueAttribute.split("@");
                if (parts.length === 2 && parts[0].length >= 1 && parts[1].length >= 3) {
                    const domainParts = parts[1].split(".");
                    if (domainParts.length > 1) {
                        return this.props.uniqueAttribute;
                    }
                }
            }
        }

        return "";
    }
}
